import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// Client bindings for Mass Outreach (server/outreach-routes.ts).

export interface OutreachList {
  id: string;
  name: string;
  industry: string | null;
  description: string | null;
  drafting_notes: string | null;
  created_at: string;
  lead_count: number;
  contacted: number;
  replied: number;
  promoted: number;
  bounced: number;
}

export interface OutreachLead {
  id: string;
  list_id: string;
  company_name: string;
  owner_name: string | null;
  owner_title: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  industry: string | null;
  revenue_estimate: string | null;
  notes: string | null;
  status: "new" | "replied" | "bounced" | "promoted";
  promoted_company_id: string | null;
  last_sent_at: string | null;
  created_at: string;
}

export interface LeadHistoryRow {
  id: string;
  status: string;
  sent_at: string | null;
  error: string | null;
  campaign_id: string;
  campaign_name: string;
  sent_from: string | null;
  sent_from_name: string | null;
}

export interface ImportReport {
  total: number;
  added: number;
  noEmail: number;
  invalidEmail: number;
  genericInbox?: number;
  duplicates: number;
  suppressed: number;
  sheetsParsed: string[];
  sample?: Partial<OutreachLead>[];
}

async function j<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as any).error || `Request failed (${res.status})`);
  return body as T;
}

export function useOutreachLists() {
  return useQuery<OutreachList[]>({
    queryKey: ["outreach", "lists"],
    queryFn: async () => j(await fetch("/api/outreach/lists", { credentials: "include" })),
  });
}

export function useOutreachLeads(listId: string | null, opts: { search?: string; status?: string; page?: number }) {
  const params = new URLSearchParams();
  if (opts.search) params.set("search", opts.search);
  if (opts.status) params.set("status", opts.status);
  params.set("page", String(opts.page ?? 1));
  return useQuery<{ total: number; page: number; pageSize: number; leads: OutreachLead[] }>({
    queryKey: ["outreach", "leads", listId, opts.search ?? "", opts.status ?? "", opts.page ?? 1],
    queryFn: async () => j(await fetch(`/api/outreach/lists/${listId}/leads?${params}`, { credentials: "include" })),
    enabled: !!listId,
    placeholderData: (prev) => prev,
  });
}

export function useLeadHistory(leadId: string | null) {
  return useQuery<LeadHistoryRow[]>({
    queryKey: ["outreach", "history", leadId],
    queryFn: async () => j(await fetch(`/api/outreach/leads/${leadId}/history`, { credentials: "include" })),
    enabled: !!leadId,
  });
}

export function useOutreachMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["outreach"] });
  const post = (url: string, body?: unknown) =>
    fetch(url, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

  return {
    createList: useMutation({
      mutationFn: async (body: { name: string; industry?: string; description?: string }) =>
        j<OutreachList>(await post("/api/outreach/lists", body)),
      onSuccess: invalidate,
    }),
    updateList: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        j(await fetch(`/api/outreach/lists/${id}`, {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        })),
      onSuccess: invalidate,
    }),
    deleteList: useMutation({
      mutationFn: async (id: string) =>
        j(await fetch(`/api/outreach/lists/${id}`, { method: "DELETE", credentials: "include" })),
      onSuccess: invalidate,
    }),
    addLead: useMutation({
      mutationFn: async (body: Record<string, unknown>) => j<OutreachLead>(await post("/api/outreach/leads", body)),
      onSuccess: invalidate,
    }),
    updateLead: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        j<OutreachLead>(await fetch(`/api/outreach/leads/${id}`, {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        })),
      onSuccess: invalidate,
    }),
    bulkDeleteLeads: useMutation({
      mutationFn: async (ids: string[]) => j<{ deleted: number }>(await post("/api/outreach/leads/bulk-delete", { ids })),
      onSuccess: invalidate,
    }),
    promoteLead: useMutation({
      mutationFn: async (id: string) => j<{ companyId: string; alreadyPromoted?: boolean }>(await post(`/api/outreach/leads/${id}/promote`)),
      onSuccess: invalidate,
    }),
    importFile: useMutation({
      mutationFn: async ({ listId, file, dryRun }: { listId: string; file: File; dryRun?: boolean }) => {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`/api/outreach/lists/${listId}/import${dryRun ? "?dryRun=1" : ""}`, {
          method: "POST", credentials: "include", body: fd,
        });
        return j<ImportReport>(res);
      },
      onSuccess: invalidate,
    }),
  };
}
